// ============================================================
// TURN EFFECTS - conditions that DO something every turn.
//
// Most conditions are modifiers: they change a number and tick down
// (engine/countdown.ts). A few instead act on their bearer once per
// turn and end on a test rather than a timer. On Fire is the archetype:
// 1d10 Energy each turn ignoring armour, ended by an Agility test.
//
// Those were tracked but inert - `Fi` is in neither COUNTDOWN_TYPES nor
// DURATION_CONDS, so it never ticked, never damaged, and never ended
// except by hand. Blood Loss has exactly the same shape and the same
// gap, which is why this is a TABLE rather than a fire-shaped branch.
//
// PURE, and deliberately dice-free: the roll is injected. Rolling here
// would make the outcome untestable and would silently consume from the
// E2E seeded dice sequence - see dev-docs/gotchas.md.
// ============================================================
import type { Target } from '../types';
import type { CharacteristicId } from './character/types';
import type { PlainTargetTestId } from './characteristicTests';
import { condType } from './conditions';
import { isImmuneTo } from './conditionEligibility';

/** How a per-turn effect ends. `null` = only a timer or manual removal. */
interface TurnEffectTest {
  /** Rolled on a character sheet when one exists. */
  charId: CharacteristicId;
  /**
   * Fallback for statblock combatants, which carry no raw characteristics.
   * `dodge` reads target.dodge.agility - Agility PLUS dodge-skill bonuses,
   * the same approximation the Flame weapon effect already makes.
   */
  plainTargetTest: PlainTargetTestId;
  /** An ally spending a Half Action to help (rules-spec.md:537). */
  assistBonus: number;
}

export interface TurnEffectRule {
  /** Condition code, matched on condType() so `S3`-style durations work. */
  code: string;
  /**
   * The test that ends it costs the combatant this action from the turn it starts (2026-10-26): Pinned's
   * shake-off is a Full Action. Advisory, like every charge on the pips - the economy never blocks.
   */
  actionCost?: 'full';
  label: string;
  /** Dice expression rolled by the CALLER, or null for a flat effect. */
  damage: string | null;
  /** Flat damage when `damage` is null. */
  flatDamage?: number;
  damageType: string;
  /** Fire bypasses armour. */
  ignoresAP: boolean;
  /**
   * Whether Toughness soaks. rules-spec.md:534 says fire ignores AP and
   * says nothing about TB, so TB applies. Kept as a field rather than
   * hard-coded so the reading can be changed without surgery.
   */
  ignoresTB: boolean;
  endsOn: TurnEffectTest | null;
  /** Verb for the passed-test narrative; 'extinguished' when absent. */
  clearVerb?: string;
}

export const TURN_EFFECTS: TurnEffectRule[] = [
  {
    code: 'Fi',
    label: 'On Fire',
    damage: '1d10',
    damageType: 'E',
    ignoresAP: true,
    ignoresTB: false,
    endsOn: { charId: 'agi', plainTargetTest: 'dodge', assistBonus: 10 },
  },
  // Snared (v8.198.2): no per-turn damage - the net just holds. The
  // official escape attempt is this Agility test each turn, ally-
  // assistable like fire. A GM-set Immobilised ('Immob') is deliberately
  // NOT here: a ruling has no escape roll.
  {
    code: 'Sn',
    label: 'Snared',
    damage: null,
    damageType: 'I',
    ignoresAP: true,
    ignoresTB: true,
    endsOn: { charId: 'agi', plainTargetTest: 'dodge', assistBonus: 10 },
    clearVerb: 'escaped',
  },
  // Pinned (v8.198.4): no damage - fear of fire. Shaken off by a Full
  // Action Willpower test (rules-spec.md); no ally assist in the spec.
  {
    code: 'Pn',
    label: 'Pinned',
    damage: null,
    damageType: 'I',
    ignoresAP: true,
    ignoresTB: true,
    endsOn: { charId: 'wp', plainTargetTest: 'willpower', assistBonus: 0 },
    clearVerb: 'shaken off',
    actionCost: 'full',
  },
];

/**
 * The per-turn effects a combatant is currently subject to, in table
 * order so two effects always resolve the same way round.
 */
export function getTurnEffects(conditions: string[] | undefined, target?: Target): TurnEffectRule[] {
  if (!conditions?.length) return [];
  const active = new Set(conditions.map((c) => condType(c)));
  return TURN_EFFECTS.filter((r) => {
    if (!active.has(r.code)) return false;
    // A condition can be present on a combatant who is immune to it - it
    // may predate the trait, or have been forced on with a shift-click.
    // Immunity has to be honoured HERE too, not just in the picker.
    if (target && isImmuneTo(target as unknown as Record<string, unknown>, r.code)) return false;
    return true;
  });
}

export interface TurnEffectDamage {
  /** Wounds actually lost, after soak. Never negative. */
  damage: number;
  /** The raw roll before soak, for the narrative breakdown. */
  rolled: number;
  /** Toughness subtracted, 0 when the effect ignores it. */
  soaked: number;
  note: string;
}

/**
 * Apply one effect's damage to a target.
 *
 * `rolled` is supplied by the caller (see the module note). Soak mirrors
 * damageCalc.ts:245 - toughnessBonus × unnatural multiplier, plus
 * daemonic protection - minus the armour term, which fire ignores.
 */
export function applyTurnEffectDamage(rule: TurnEffectRule, target: Target, rolled: number): TurnEffectDamage {
  const tb = rule.ignoresTB
    ? 0
    : (target.toughnessBonus || 0) * (target.unnaturalToughness || 1) + (target.daemonicProtection || 0);

  const damage = Math.max(0, rolled - tb);
  const soakNote = rule.ignoresTB ? '' : ` − ${tb} TB`;
  const apNote = rule.ignoresAP ? ', ignores AP' : '';
  const note =
    damage > 0
      ? `🔥 ${rule.label}: ${rolled}${soakNote} = ${damage} ${rule.damageType} damage${apNote}`
      : `🔥 ${rule.label}: ${rolled}${soakNote} - no damage${apNote}`;

  return { damage, rolled, soaked: tb, note };
}

/** Remove one effect's condition - what a passed test does. */
export function clearTurnEffect(conditions: string[], rule: TurnEffectRule): string[] {
  return conditions.filter((c) => condType(c) !== rule.code);
}
